import React, { useEffect, useMemo, useState } from 'react';
import { SvgElement, ColorValue, MediaType, StaticOrDynamic, zImageStaticSource } from '../../../core/template.schemas';
import { useTemplateStore } from '../../hooks/useTemplateStore';
import { Label } from '@/ui-components/ui/label';
import { Component, Palette, Image as ImageIcon, Trash2, RefreshCw } from 'lucide-react';
import { CommonProperties } from './common';
import { AdvancedStaticDynamicSelector, elementAttributeRegistry } from '../../registry';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui-components/ui/select';
import { resolveImageSource, ResolveContext } from '../../../core/runtime/resolve';
import { Button } from '@/ui-components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui-components/ui/tooltip';

interface SvgPropertiesProps {
  element: SvgElement;
  onOpenSlotPanel?: () => void;
}

export const SvgProperties: React.FC<SvgPropertiesProps> = ({ element, onOpenSlotPanel }) => {
  const updateElement = useTemplateStore(s => s.updateElement);
  const slots = useTemplateStore(s => s.template.slots);
  
  const resolveContext = useMemo<ResolveContext>(() => ({
    selected: {},
    overrides: {},
    templateSlots: slots
  }), [slots]);

  const [availableColors, setAvailableColors] = useState<string[]>([]);
  const [isLoadingColors, setIsLoadingColors] = useState(false);

  // Get attribute mappings
  const svgMapping = elementAttributeRegistry.get('svg');
  const sourceAttribute = svgMapping?.attributes.find(attr => attr.attributeKey === 'source');

  const handlePropertyChange = (property: keyof SvgElement, value: any) => {
    const updates: Partial<SvgElement> = { [property]: value };
    
    // Robustness: Cleanup logic when switching fillMode
    if (property === 'fillMode') {
      if (value === 'original') {
        updates.colorMapping = {};
        updates.monochromeColor = undefined;
      } else if (value === 'monochrome') {
        updates.colorMapping = {};
      } else if (value === 'custom') {
        updates.monochromeColor = undefined;
      }
    }
    
    updateElement(element.id, updates);
  };

  const handleSourceChange = (
    sourceValue: StaticOrDynamic<any, MediaType> | null,
    meta?: { width?: number; height?: number }
  ) => {
    // If a new image is selected, update dimensions if needed (optional)
     if (sourceValue) {
      if (sourceValue.mode === 'static' && !sourceValue.staticValue) {
        handlePropertyChange('source', {
          ...sourceValue,
          staticValue: zImageStaticSource.parse({}),
        });
      } else {
        handlePropertyChange('source', sourceValue);
      }
    } else {
      handlePropertyChange('source', {
        mode: 'static',
        mediaType: 'image',
        staticValue: zImageStaticSource.parse({}),
      });
    }
  };

  // Fetch SVG and extract colors
  useEffect(() => {
    const src = resolveImageSource(element.source, resolveContext);
    if (!src || (element.source.mode === 'static' && !element.source.staticValue?.path && !element.source.staticValue?.relativePath)) {
      setAvailableColors([]);
      return;
    }

    let isActive = true;
    setIsLoadingColors(true);
    
    fetch(src)
      .then(res => res.text())
      .then(text => {
        if (!isActive) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'image/svg+xml');
        const svg = doc.documentElement;
        const colors = new Set<string>();
        
        const elements = svg.querySelectorAll('*');
        elements.forEach(el => {
            const fill = el.getAttribute('fill');
            if (fill && fill !== 'none' && fill !== 'currentColor' && !fill.startsWith('url(')) colors.add(fill);
            const stroke = el.getAttribute('stroke');
            if (stroke && stroke !== 'none' && stroke !== 'currentColor' && !stroke.startsWith('url(')) colors.add(stroke);
            
            // Handle style
            const style = el.getAttribute('style');
            if(style) {
                 // Simple regex for fill/stroke in style
                 const fillMatch = style.match(/fill:\s*([^;]+)/);
                 if(fillMatch && fillMatch[1] !== 'none' && !fillMatch[1].startsWith('url(')) colors.add(fillMatch[1].trim());
                 const strokeMatch = style.match(/stroke:\s*([^;]+)/);
                 if(strokeMatch && strokeMatch[1] !== 'none' && !strokeMatch[1].startsWith('url(')) colors.add(strokeMatch[1].trim());
            }
        });
        setAvailableColors(Array.from(colors));
      })
      .catch(err => {
        console.error("Failed to load SVG for properties", err);
        if (isActive) setAvailableColors([]);
      })
      .finally(() => {
        if (isActive) setIsLoadingColors(false);
      });

    return () => { isActive = false; };
  }, [element.source, resolveContext]);

  const updateColorMapping = (originalColor: string, newColor: ColorValue | null) => {
     const newMapping = { ...(element.colorMapping || {}) };
     if (newColor) {
         newMapping[originalColor] = newColor;
     } else {
         delete newMapping[originalColor];
     }
     handlePropertyChange('colorMapping', newMapping);
  };

  const clearColorMapping = () => {
    handlePropertyChange('colorMapping', {});
  };

  return (
    <div className="space-y-4">
      <CommonProperties element={element} />
      
      <div className="space-y-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Component className="w-3.5 h-3.5 text-gray-500" />
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">SVG / Icon</Label>
        </div>

        {/* Source Section */}
        {sourceAttribute && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-3 h-3 text-gray-400" />
              <Label className="text-[11px] text-gray-500 font-medium">Quelle</Label>
            </div>
            <div className="bg-gray-50/30 p-2 rounded-xl border border-gray-100">
              <AdvancedStaticDynamicSelector
                label="SVG Quelle"
                value={element.source}
                onChange={handleSourceChange}
                compatibleMediaTypes={sourceAttribute.compatibleMediaTypes}
                required={sourceAttribute.required}
                onOpenSlotPanel={onOpenSlotPanel}
                allowedMimeTypes={['image/svg+xml']}
              />
            </div>
          </div>
        )}

        {/* Fill Mode Section */}
        <div className="space-y-3 pt-1">
          <div className="flex items-center gap-2">
            <Palette className="w-3 h-3 text-gray-400" />
            <Label className="text-[11px] text-gray-500 font-medium">Farb-Modus</Label>
          </div>
          
          <div className="bg-gray-50/30 p-2 rounded-xl border border-gray-100 space-y-3">
             <Select 
                value={element.fillMode} 
                onValueChange={(val: any) => handlePropertyChange('fillMode', val)}
             >
                <SelectTrigger className="h-8 text-[11px] bg-white border-gray-200 rounded-lg">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="original" className="text-[11px]">Originalfarben</SelectItem>
                    <SelectItem value="monochrome" className="text-[11px]">Monochrom (Einfärben)</SelectItem>
                    <SelectItem value="custom" className="text-[11px]">Individuell (Farb-Mapping)</SelectItem>
                </SelectContent>
             </Select>

            {/* Monochrome Color Picker */}
            {element.fillMode === 'monochrome' && (
              <div className="pt-1">
                <AdvancedStaticDynamicSelector
                    label="Farbe wählen"
                    value={element.monochromeColor || { mode: 'static', mediaType: 'color', staticValue: '#000000' }}
                    onChange={(val) => handlePropertyChange('monochromeColor', val)}
                    compatibleMediaTypes={['color']}
                    required={true}
                    onOpenSlotPanel={onOpenSlotPanel}
                />
              </div>
            )}
          </div>
        </div>

        {/* Custom Color Mapping Section */}
        {element.fillMode === 'custom' && (
            <div className="space-y-3 pt-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <RefreshCw className={`w-3 h-3 text-gray-400 ${isLoadingColors ? 'animate-spin' : ''}`} />
                    <Label className="text-[11px] text-gray-500 font-medium">Farben anpassen</Label>
                  </div>
                  {availableColors.length > 0 && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-gray-400 hover:text-red-500"
                            onClick={clearColorMapping}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-[10px]">Alle Anpassungen zurücksetzen</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>

                <div className="space-y-2">
                  {isLoadingColors ? (
                    <div className="py-4 flex flex-col items-center justify-center gap-2 bg-gray-50/30 rounded-xl border border-dashed border-gray-200">
                      <RefreshCw className="w-4 h-4 text-gray-300 animate-spin" />
                      <p className="text-[10px] text-gray-400">Analysiere SVG Farben...</p>
                    </div>
                  ) : availableColors.length === 0 ? (
                    <div className="py-4 flex flex-col items-center justify-center gap-2 bg-gray-50/30 rounded-xl border border-dashed border-gray-200 text-center px-4">
                      <p className="text-[10px] text-gray-400 italic">Keine anpassbaren Farben in diesem SVG gefunden.</p>
                    </div>
                  ) : (
                    <div className="grid gap-2">
                      {availableColors.map((color, i) => (
                        <div key={color} className="group relative flex flex-col gap-1.5 p-2 rounded-xl border border-gray-100 bg-gray-50/30 hover:border-gray-200 transition-colors">
                            <div className="flex items-center gap-2">
                              <div 
                                  className="w-4 h-4 rounded-md border border-gray-200 shadow-sm flex-shrink-0"
                                  style={{ backgroundColor: color }}
                                  title={`Original: ${color}`}
                              />
                              <span className="text-[10px] font-mono text-gray-400 truncate uppercase">{color}</span>
                            </div>
                            
                            <AdvancedStaticDynamicSelector
                                label={`Farbe ${i + 1}`}
                                value={element.colorMapping?.[color] || { mode: 'static', mediaType: 'color', staticValue: color }}
                                onChange={(val) => updateColorMapping(color, val as ColorValue)}
                                compatibleMediaTypes={['color']}
                                required={true}
                                onOpenSlotPanel={onOpenSlotPanel}
                            />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            </div>
        )}

      </div>
    </div>
  );
};
